// import { AppStateService } from '@/service/app/app-state.service'
import { Injectable, OnDestroy, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { interval, Observable, of, Subscription } from 'rxjs'
import {
  catchError,
  map,
  pairwise,
  shareReplay,
  switchMap,
} from 'rxjs/operators'
import { MachineStateTransport } from '../models/states.models'
import { TouaregControlStateParamsService } from './touareg-control-state-params.service'
import { TouaregControlStateMachineService } from './touareg-control-state-machine.service'
import moment, { Moment } from 'moment'

export interface MonitorDataPool {
  score: number | null
  value: number
}

export interface MonitorDataScore {
  score: 0 | 1 | 2 | 3 | 4 | 5 | null
  average: number
}
export interface HealthMonitorData {
  active: boolean
  status: 'connected' | 'waiting' | null
  tooMuchLatency: boolean | null
  pingDelay: number | null
  stateUpdateDateDelay: number | null
  timeshift: number | null
  dateOffset: number | null
  pingScore: MonitorDataScore | null
  stateUpdateScore: MonitorDataScore | null
  statePureUpdateScore: MonitorDataScore | null
  timeshiftScore: MonitorDataScore | null
  minScore: 0 | 1 | 2 | 3 | 4 | 5 | null
  dateOffsetIndicator: boolean | null
}

const INITIAL_HEALTH: HealthMonitorData = {
  active: false,
  status: null,
  tooMuchLatency: null,
  pingDelay: null,
  stateUpdateDateDelay: null,
  timeshift: null,
  dateOffset: null,
  pingScore: null,
  stateUpdateScore: null,
  statePureUpdateScore: null,
  timeshiftScore: null,
  dateOffsetIndicator: null,
  minScore: null,
}

export interface HealthMonitorSettings {
  measurePoolLength: number
  pingMinDelay: number
  pingMaxDelay: number
  stateUpdateDateMinDelay: number
  stateUpdateDateMaxDelay: number
  timeshiftMinDelay: number
  timeshiftMaxDelay: number
}

const INITIAL_HEALTH_MONITOR_SETTINGS = {
  measurePoolLength: 5,
  pingMinDelay: 300,
  pingMaxDelay: 1500,
  dateOffsetMax: 500,
  timeshiftMaxDelay: 5000,
  timeshiftMinDelay: 2200,
  stateUpdateDateMaxDelay: 3000,
  stateUpdateDateMinDelay: 1500,
}

@Injectable()
export class TouaregControlStateHealthMonitorService implements OnDestroy {
  public readonly health$: Observable<HealthMonitorData>
  public _health: HealthMonitorData = INITIAL_HEALTH

  private readonly _healthMonitorSettings =
    signal<HealthMonitorSettings>(INITIAL_HEALTH_MONITOR_SETTINGS)
  public readonly healthMonitorSettings =
    this._healthMonitorSettings.asReadonly()
  public readonly healthMonitorSettings$: Observable<HealthMonitorSettings> =
    toObservable(this._healthMonitorSettings)

  private _machineStateSubs$!: Subscription
  private _dateOffset: number | null = null

  private _updateStateScoreMap!: number[]
  private _timeshiftScoreMap!: number[]
  private _pingScoreMap!: number[]

  private _lastHeartbeatDate: Moment | null = null
  private _timeshiftPool: MonitorDataPool[] = []
  private _updatePool: MonitorDataPool[] = []
  private _pureUpdatePool: MonitorDataPool[] = []
  private _pingPool: MonitorDataPool[] = []

  private _pingDelay: number | null = null
  private _pingScore: MonitorDataScore | null = null
  private _timeshift: number | null = null
  private _timeshiftScore: MonitorDataScore | null = null
  private _stateUpdateDelay: number | null = null
  private _stateUpdateScore: MonitorDataScore | null = null
  private _statePureUpdateScore: MonitorDataScore | null = null
  private _dateOffsetIndicator: boolean | null = null

  get health(): HealthMonitorData {
    return this._health
  }

  get _poolLength(): number {
    return this._healthMonitorSettings()?.measurePoolLength || 1
  }

  constructor(
    private _params: TouaregControlStateParamsService,
    private _machine: TouaregControlStateMachineService
  ) {
    // On créé les score mappers
    this._createScoreMappers(this._healthMonitorSettings())

    // On créé de l'observable
    this.health$ = this._params.params$.pipe(
      switchMap((params) => {
        const { connect, active, beaconId } = params
        const defaultHealth = { ...INITIAL_HEALTH, active } as HealthMonitorData
        if (active && connect && beaconId) {
          return interval(1000).pipe(
            map(() => this._analyse()),
            catchError((e) => of(defaultHealth))
          )
        }
        return of(defaultHealth)
      }),
      shareReplay(1)
    )

    // On écoute la machine elle-même
    this._machineStateSubs$ = this._machine.purefromStateMachine$
      .pipe(pairwise())
      .subscribe((data) => this._storeMachineMetrics(data))
  }

  private _storeMachineMetrics(states: (MachineStateTransport | null)[]) {
    // Si pas de premiere trame recue de la machine
    // On stoppe et on reset
    if (!states[0]) {
      this._resetAll()
      return
    }

    // on stocke la date de réception de l'etat
    this._lastHeartbeatDate = moment()

    // on stope si pas de state ou si on a pas récupéré les confs
    if (!states[1] || this._hasNoMaxMetrics()) {
      this._resetAll()
      return
    }

    // on calcule et on stocke les scores
    this._checkPingScore(states[1])
    this._checkTimeshiftScore(states[1])
    this._checkStateUpdateScore(states[1])
  }

  private _analyse(): HealthMonitorData {
    if (!this._lastHeartbeatDate) {
      return {
        ...INITIAL_HEALTH,
        active: this._params.isActive,
        status: 'waiting',
      }
    }

    // On créé la nouvelle trame de santé
    const health = {
      ...this._health,
      active: true,
      status: 'connected',
      dateOffset: this._dateOffset,
      dateOffsetIndicator: this._dateOffsetIndicator,
      pingDelay: this._pingDelay,
      pingScore: this._pingScore,
      stateUpdateDateDelay: this._stateUpdateDelay,
      timeshift: this._timeshift,
      stateUpdateScore: this._stateUpdateScore,
      statePureUpdateScore: this._statePureUpdateScore,
      timeshiftScore: this._timeshiftScore,
    } as HealthMonitorData

    // Mutation de l'objet healt
    this._checkAllLatencies(health)

    // on stocke et renvoie l'etat de santé
    this._health = health
    return health
  }

  private _resetAll() {
    this._dateOffset = null
    this._pingScore = null
    this._pingDelay = null
    this._timeshift = null
    this._timeshiftScore = null
    this._timeshiftPool = []
    this._stateUpdateDelay = null
    this._stateUpdateScore = null
    this._statePureUpdateScore = null
    this._dateOffsetIndicator = null
    this._updatePool = []
    this._pureUpdatePool = []
  }

  private _checkPingScore(state: MachineStateTransport) {
    // on stocke les metrics du ping network
    this._pingDelay = state.ping
    this._dateOffset = state.dateOffset
    this._dateOffsetIndicator =
      state.dateOffset <= INITIAL_HEALTH_MONITOR_SETTINGS.dateOffsetMax

    // On check le score
    // on en déuit une score
    const lastPingScore = this._checkScore(this._pingScoreMap, this._pingDelay)

    // on ajoute ce score au pool
    this._pingPool.unshift({
      score: lastPingScore,
      value: this._pingDelay,
    })
    if (this._pingPool.length > this._poolLength) {
      this._pingPool.length = this._poolLength
    }

    // on récupère le score le plus récurrent dans le pool
    this._pingScore = this._getScoreFromPool(this._pingPool)
  }

  private _checkTimeshiftScore(state: MachineStateTransport) {
    // on récupère le décalage entre la date de réception de l'état
    // et la date de l'évenement à l'orgine de ce nouvel etat
    // _lastHeartbeatDate est toujours défini ici (assigné dans _storeMachineMetrics avant appel)
    this._timeshift = this._lastHeartbeatDate!.diff(state.content.genesis_time)

    // On redresse le timeshift avec le décalage d'horloge device/server
    this._timeshift -= state.dateOffset

    // on en déuit une score
    const lastTimeshiftScore = this._checkScore(
      this._timeshiftScoreMap,
      this._timeshift
    )

    // on ajoute le score au pool
    this._timeshiftPool.unshift({
      score: lastTimeshiftScore,
      value: this._timeshift,
    })
    if (this._timeshiftPool.length > this._poolLength) {
      this._timeshiftPool.length = this._poolLength
    }

    // on récupère le score le plus récurrent dans le pool
    this._timeshiftScore = this._getScoreFromPool(this._timeshiftPool)
  }

  private _checkStateUpdateScore(state: MachineStateTransport) {
    // on récupère le décalage entre la date de réception de l'état
    // et la date de post du nouvel etat
    // _lastHeartbeatDate est toujours défini ici (assigné dans _storeMachineMetrics avant appel)
    this._stateUpdateDelay = this._lastHeartbeatDate!.diff(
      state.content.state_update_date
    )

    // Ici, le delay n'est pas corrigé par l'offset des horloges devces/server
    // On stocke cette valeur "pure"
    this._pureUpdatePool.unshift({
      score: null,
      value: this._stateUpdateDelay,
    })

    // Ensuite, on corrige le state update avec l'offset d'horloge device/server
    this._stateUpdateDelay -= state.dateOffset

    // on en déduit une score
    const lastUpdateScore = this._checkScore(
      this._updateStateScoreMap,
      this._stateUpdateDelay
    )

    // on ajoute le score au pool
    this._updatePool.unshift({
      score: lastUpdateScore,
      value: this._stateUpdateDelay,
    })

    // On ratiboise les pools arrays
    if (this._updatePool.length > this._poolLength) {
      this._updatePool.length = this._poolLength
      this._pureUpdatePool.length = this._poolLength
    }

    // on récupère le score le plus récurrent dans le pool
    this._stateUpdateScore = this._getScoreFromPool(this._updatePool)
    this._statePureUpdateScore = {
      score: null,
      average: this._getMean(this._pureUpdatePool),
    }
  }

  private _getMean(pool: MonitorDataPool[]): number {
    if (pool?.length) {
      let total = 0
      for (let i = 0, l = pool.length; i < l; i += 1) {
        total = pool[i].value
      }
      return total / pool.length
    }
    return 0
  }

  private _getScoreFromPool(pool: MonitorDataPool[]): {
    score: 0 | 1 | 2 | 3 | 4 | 5
    average: number
  } {
    const sc: Record<number, number> = {}
    let max = 0
    let bestScore: 0 | 1 | 2 | 3 | 4 | 5 = 0
    let total = 0
    let i = 0
    pool.forEach((data) => {
      i += 1
      // Ce pool ne reçoit que des scores calculés (jamais null)
      const s = data.score as 0 | 1 | 2 | 3 | 4 | 5
      total += data.value
      sc[s] ? (sc[s] += 1) : (sc[s] = 1)
      if (max < sc[s]) {
        max = sc[s]
        bestScore = s
      }
    })
    return {
      score: bestScore as 0 | 1 | 2 | 3 | 4 | 5,
      average: Math.round(total / i),
    }
  }

  private _hasNoMaxMetrics(): boolean {
    // On arrete là si pas de settings depuis les global params api
    return (
      !this._pingScoreMap ||
      !this._updateStateScoreMap ||
      !this._timeshiftScoreMap
    )
  }

  private _checkAllLatencies(health: HealthMonitorData): void {
    const { pingScore, stateUpdateScore, timeshiftScore } = health

    // on calcule le score minimal de la trame
    const minScore = Math.min(
      pingScore?.score || 0,
      stateUpdateScore?.score || 0,
      timeshiftScore?.score || 0
    )

    // On check si trop de latence
    if (minScore === 0) {
      health.tooMuchLatency = true
    } else {
      health.tooMuchLatency = false
    }

    // on ajoute le score minimal de la trame
    health.minScore = minScore as 0 | 1 | 2 | 3 | 4 | 5
  }

  private _createScoreMappers(options: HealthMonitorSettings) {
    const {
      stateUpdateDateMinDelay,
      stateUpdateDateMaxDelay,
      timeshiftMinDelay,
      timeshiftMaxDelay,
      pingMinDelay,
      pingMaxDelay,
    } = options || {}

    const updateVal = (stateUpdateDateMaxDelay - stateUpdateDateMinDelay) / 4
    const timeshiftVal = (timeshiftMaxDelay - timeshiftMinDelay) / 4
    const pingVal = (pingMaxDelay - pingMinDelay) / 4

    this._pingScoreMap = [
      pingMaxDelay,
      pingMinDelay + pingVal * 3,
      pingMinDelay + pingVal * 2,
      pingMinDelay + pingVal,
      pingMinDelay,
    ]

    this._updateStateScoreMap = [
      stateUpdateDateMaxDelay,
      stateUpdateDateMinDelay + updateVal * 3,
      stateUpdateDateMinDelay + updateVal * 2,
      stateUpdateDateMinDelay + updateVal,
      stateUpdateDateMinDelay,
    ]
    this._timeshiftScoreMap = [
      timeshiftMaxDelay,
      timeshiftMinDelay + timeshiftVal * 3,
      timeshiftMinDelay + timeshiftVal * 2,
      timeshiftMinDelay + timeshiftVal,
      timeshiftMinDelay,
    ]
  }

  private _checkScore(mapper: number[], value: number): 0 | 1 | 2 | 3 | 4 | 5 {
    for (let i = 0, l = mapper.length; i < l; i += 1) {
      if (value > mapper[i]) {
        return i as any
      }
    }
    return 5
  }

  ngOnDestroy() {
    if (!!this._machineStateSubs$) this._machineStateSubs$.unsubscribe()
  }
}
